import { Box, FormControl, FormLabel, TextField, Select, MenuItem } from '@mui/material';
import { map } from 'lodash';
import { useContext } from 'react';
import { type Control, Controller, type FieldErrors, type UseFormGetValues } from 'react-hook-form';

import type { CompanyFormValues } from './types';

import EssealPhoneInput from '@/components/EssealPhoneInput';
import PermissionContext from '@/Contexts/Permissions';
import { PermissionActionEnum, PermissionEntityEnum } from '@/graphql/generated/graphql';
import { COMPANY_TYPE_OPTIONS } from '@/utils/constants';
import { isValidEmail } from '@/utils/userInformation';

interface CompanyFormProps {
  control: Control<CompanyFormValues, any, CompanyFormValues>;
  formErrors: FieldErrors<CompanyFormValues>;
  getValues: UseFormGetValues<CompanyFormValues>;
}

const CompanyForm: React.FC<CompanyFormProps> = ({ control, formErrors, getValues }) => {
  const { canPerformAction } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { CompanySettings } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, CompanySettings);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Company Name is required',
          }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel htmlFor="company-name" sx={{ fontSize: 12, mb: 0.5 }}>
                Company Name
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                id="company-name"
                variant="outlined"
                placeholder="Enter your company name"
                error={field.value?.trim() === ''}
                helperText={field.value?.trim() === '' ? 'Company Name is required' : ''}
                disabled={!hasPermission}
              />
            </FormControl>
          )}
        />

        <Controller
          name="tradingName"
          control={control}
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel htmlFor="trading-name" sx={{ fontSize: 12, mb: 0.5 }}>
                Trading Name
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                id="trading-name"
                variant="outlined"
                placeholder="If different from company name"
                disabled={!hasPermission}
              />
            </FormControl>
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Controller
          name="companyNumber"
          control={control}
          rules={{ required: 'Company Number is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1, minWidth: 140 }}>
              <FormLabel htmlFor="company-number" sx={{ fontSize: 12, mb: 0.5 }}>
                Company Number
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                id="company-number"
                variant="outlined"
                placeholder="e.g. 12345678"
                error={formErrors.companyNumber?.type === 'required'}
                helperText={formErrors.companyNumber?.type === 'required' ? 'Company Number is required' : ''}
                disabled={!hasPermission}
              />
            </FormControl>
          )}
        />

        <Controller
          name="vatNumber"
          control={control}
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel htmlFor="vat-number" sx={{ fontSize: 12, mb: 0.5 }}>
                VAT Number
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                size="small"
                id="vat-number"
                variant="outlined"
                placeholder="e.g. GB123456789"
                disabled={!hasPermission}
              />
            </FormControl>
          )}
        />

        <Controller
          name="companyType"
          control={control}
          rules={{ required: 'Company Type is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1, minWidth: 280 }}>
              <FormLabel htmlFor="company-type" sx={{ fontSize: 12, mb: 0.5 }}>
                Company Type
              </FormLabel>
              <Select {...field} size="small" id="company-type" disabled={!hasPermission}>
                {map(COMPANY_TYPE_OPTIONS, (opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Controller
          name="primaryPhone"
          control={control}
          rules={{ required: 'Primary Phone is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel htmlFor="company-phone" sx={{ fontSize: 12, mb: 0.5 }}>
                Primary Phone
              </FormLabel>
              <EssealPhoneInput
                {...field}
                defaultCountry="GB"
                id="company-phone"
                placeholder="Enter your company phone"
                style={{ width: '100%' }}
                size="sm"
                required
                disabled={!hasPermission}
              />
            </FormControl>
          )}
        />

        <Controller
          name="primaryEmail"
          control={control}
          rules={{ required: 'Primary Email is required' }}
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel htmlFor="company-email" sx={{ fontSize: 12, mb: 0.5 }}>
                Primary Email
              </FormLabel>
              <TextField
                {...field}
                required
                fullWidth
                type="email"
                size="small"
                variant="outlined"
                id="company-email"
                placeholder="Enter your company email"
                error={formErrors.primaryEmail?.type === 'required'}
                disabled={!hasPermission}
                helperText={
                  formErrors.primaryEmail?.type === 'required'
                    ? 'Email is required.'
                    : !isValidEmail(getValues('primaryEmail') as string)
                      ? 'Invalid Email'
                      : ''
                }
              />
            </FormControl>
          )}
        />
      </Box>
    </>
  );
};

export default CompanyForm;
